import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertTriangle, MessageCircleQuestion, FileWarning, UserCheck, FolderClock, RefreshCw } from 'lucide-react';
import { compliance } from '@/api/client';
import { fmtDateTime } from '@/lib/dates.js';
import { errorText } from '@/lib/permissionError';

const POLL_MS = 60_000;

interface Props {
  onOpenTab?: (key: 'queries' | 'audit' | 'access') => void;
}

export function ComplianceOverviewTab({ onOpenTab }: Props) {
  const q = useQuery({
    queryKey: ['compliance-overview'],
    queryFn: () => compliance.overview(),
    refetchInterval: POLL_MS,
  });

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-ink-muted">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="ml-2 text-sm">Loading compliance snapshot…</span>
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className="card p-4 flex items-start gap-2 text-sm text-red-700">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>{errorText(q.error, 'Failed to load the compliance overview.')}</span>
      </div>
    );
  }

  const o = q.data;
  const criticalTasks = o.tasks.by_severity.critical + o.tasks.by_severity.high;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-ink-muted">
          Snapshot as of {fmtDateTime(o.generated_at)} · {o.studies_active} active {o.studies_active === 1 ? 'study' : 'studies'} at this site
        </p>
        <button
          type="button"
          onClick={() => q.refetch()}
          disabled={q.isFetching}
          className="flex items-center gap-1.5 rounded-lg border border-surface-border px-2.5 py-1.5 text-[12px] font-medium text-ink-secondary hover:bg-surface-muted/50 disabled:opacity-40"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${q.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={MessageCircleQuestion}
          label="Open queries"
          value={o.queries.open}
          tone={o.queries.aging.gt_14d > 0 ? 'warn' : 'neutral'}
          footnote={
            o.queries.oldest_open_days === null
              ? 'None open'
              : `Oldest ${o.queries.oldest_open_days}d · ${o.queries.aging.gt_14d} over 14d`
          }
          onClick={onOpenTab ? () => onOpenTab('queries') : undefined}
        />
        <StatCard
          icon={FileWarning}
          label="Open eISF tasks"
          value={o.tasks.open}
          tone={criticalTasks > 0 ? 'critical' : 'neutral'}
          footnote={`${o.tasks.by_severity.critical} critical · ${o.tasks.by_severity.high} high`}
        />
        <StatCard
          icon={FolderClock}
          label="Expired documents"
          value={o.tasks.expired_documents}
          tone={o.tasks.expired_documents > 0 ? 'critical' : 'ok'}
          footnote={`${o.tasks.protocol_amendments} protocol-amendment review${o.tasks.protocol_amendments === 1 ? '' : 's'}`}
        />
        <StatCard
          icon={UserCheck}
          label="Subjects pending re-consent"
          value={o.reconsent.subjects_pending}
          tone={o.reconsent.subjects_pending > 0 ? 'warn' : 'ok'}
          footnote={o.reconsent.subjects_pending === 0 ? 'All current' : 'Awaiting re-consent'}
        />
      </div>

      <div className="card p-4">
        <h3 className="text-[13px] font-semibold text-ink-primary mb-3">Open queries by age</h3>
        <AgingBars aging={o.queries.aging} total={o.queries.open} />
      </div>
    </div>
  );
}

const TONE_STYLES = {
  neutral: { ring: 'border-surface-border', value: 'text-ink-primary', icon: 'text-ink-muted' },
  ok: { ring: 'border-emerald-200', value: 'text-emerald-700', icon: 'text-emerald-500' },
  warn: { ring: 'border-amber-200', value: 'text-amber-700', icon: 'text-amber-500' },
  critical: { ring: 'border-red-200', value: 'text-red-700', icon: 'text-red-500' },
} as const;

function StatCard({
  icon: Icon, label, value, footnote, tone, onClick,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  footnote: string;
  tone: keyof typeof TONE_STYLES;
  onClick?: () => void;
}) {
  const s = TONE_STYLES[tone];
  const clickable = !!onClick;
  return (
    <div
      className={`card border ${s.ring} p-4 ${clickable ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''}`}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
        <Icon className={`h-4 w-4 ${s.icon}`} />
      </div>
      <div className={`mt-2 text-3xl font-bold tabular-nums ${s.value}`}>{value}</div>
      <div className="mt-1 text-[11px] text-ink-muted">{footnote}</div>
    </div>
  );
}

const AGING_META: Array<{ key: 'lt_3d' | '3_7d' | '7_14d' | 'gt_14d'; label: string; bar: string }> = [
  { key: 'lt_3d', label: '< 3 days', bar: 'bg-emerald-400' },
  { key: '3_7d', label: '3–7 days', bar: 'bg-sky-400' },
  { key: '7_14d', label: '7–14 days', bar: 'bg-amber-400' },
  { key: 'gt_14d', label: '> 14 days', bar: 'bg-red-400' },
];

function AgingBars({ aging, total }: { aging: Record<'lt_3d' | '3_7d' | '7_14d' | 'gt_14d', number>; total: number }) {
  if (total === 0) {
    return <p className="text-[12px] text-ink-muted">No open queries.</p>;
  }
  return (
    <div className="space-y-2">
      {AGING_META.map(({ key, label, bar }) => {
        const count = aging[key];
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <div key={key} className="flex items-center gap-3">
            <span className="w-20 shrink-0 text-[11px] text-ink-secondary">{label}</span>
            <div className="flex-1 h-2.5 rounded-full bg-surface-muted overflow-hidden">
              <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="w-8 shrink-0 text-right text-[11px] tabular-nums text-ink-primary">{count}</span>
          </div>
        );
      })}
    </div>
  );
}
